import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, StyleSheet, Text } from 'react-native'

import { publishEvent } from '@zet/analytics'
import LazyHOC from '@zet/common/components/lazyHoc'
import {
  B1,
  B2,
  Button,
  colors,
  H5,
  SH1,
  SH3,
  Stack,
  XStack,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import {
  privacyPolicyForExperianLink,
  termsAndConditionsForExperianLink,
} from '@zet/feature-brands/utils/constants'
import { i18n } from '@zet/localization'
import { isApp, KeyBoardWrapper } from '@zet/utils'
import { navigateTo } from '@zet/utils/navigation'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'

import { IRequestData } from '../../api/functions/customerAdditionStateMachine'
import {
  useAddCustomerNextState,
  useAddCustomerNextStateWeb,
} from '../../api/hooks'
import { useSecondaryPhoneNumberResendOtp } from '../../api/hooks/useSecondaryPhoneNumberResendOtp'
import useStore from '../../store'

const isWeb = Platform.OS === 'web'

const VerifySecondaryPhoneOtp = ({
  route: {
    params: { customerId },
  },
}: {
  route: {
    params: {
      customerId: number | undefined
    }
  }
}) => {
  const [value, setValue] = useState<string>('')
  const [timer, setTimer] = useState<number>(30)
  const [disableResend, setDisableResend] = useState<boolean>(false)
  const timerRef = useRef(0)
  const ref = useBlurOnFulfill({ value, cellCount: 4 })
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  })
  const { mutate, isLoading: isExperimentsLoading } = useAddCustomerNextState()
  const { mutate: mutateWeb } = useAddCustomerNextStateWeb()
  const { mutate: mutateResendOtp } = useSecondaryPhoneNumberResendOtp()
  const { selectedCustomer } = useStore((state) => state)

  const interval = 1000
  let prev = 0
  let diff = 0

  const handleSubmit = () => {
    const requestBody = {
      customerId: customerId as number,
      otp: value,
      secondaryPhoneNumber: selectedCustomer?.secondaryPhoneNumber,
      screenName: 'SHOWN_SECONDARY_OTP_SCREEN',
    }
    mutate(requestBody as IRequestData)
  }

  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }
      if (prev > current) diff = 60 - prev + current
      else diff = current - prev
      prev = date.getSeconds()
      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  const handleButtonDisablity = () => {
    if (Object.values(value).join('').length === 4) return false
    else return true
  }
  function openTermsAndConditions() {
    if (isWeb) {
      window.open(termsAndConditionsForExperianLink)
    } else {
      navigateTo('TermsOfUse', { isCustomer: true, isExperian: true })
    }
    publishEvent('click_action', {
      item_name: 'verify_secondary_phone_customer_terms_of_use',
    })
  }

  function openPrivacyPolicy() {
    if (isWeb) {
      window.open(privacyPolicyForExperianLink)
    } else {
      navigateTo('PrivacyPolicy', { isCustomer: true, isExperian: true })
      publishEvent('click_action', {
        item_name: 'verify_secondary_phone_customer_privacy_policy',
      })
    }
  }

  const resendOtp = () => {
    mutateResendOtp(
      {
        customerId: customerId,
        isApp: isApp,
        secondaryPhoneNumber: selectedCustomer?.secondaryPhoneNumber,
      },
      {
        onSuccess: (data) => {
          if (data?.lockedTill) {
            setDisableResend(true)
          }
        },
      },
    )
  }

  const onPressResendButton = () => {
    prev = 0
    diff = 0
    timerFunction()
    resendOtp()
  }

  useEffect(() => {
    otpTimer()
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  const editPhoneNumber = () => {
    const requestData = {
      customerId,
      screenName: 'BACK',
    } as IRequestData
    if (isWeb) {
      mutateWeb(requestData)
    } else {
      mutate(requestData)
    }
  }

  return (
    <LazyHOC>
      <KeyBoardWrapper
        className="flex-1 bg-white"
        BottomSheet={
          <>
            <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
              <XStack className="my-2">
                <B2 className="text-grays-70">
                  {i18n.t('verifySecondaryPhNum.agree')}
                  <B2
                    onPress={openTermsAndConditions}
                    className="text-primary-100 font-[Graphie-Bold]"
                  >
                    {' '}
                    {i18n.t('verifySecondaryPhNum.terms')}
                  </B2>
                  <B2 className="text-primary-100">
                    {' '}
                    {i18n.t('verifySecondaryPhNum.and')}{' '}
                  </B2>
                  <B2
                    onPress={openPrivacyPolicy}
                    className="text-primary-100 font-[Graphie-Bold]"
                  >
                    {i18n.t('verifySecondaryPhNum.privacyPolicy')}.
                  </B2>
                </B2>
              </XStack>
              <Button
                size="lg"
                className={`w-full rounded py-[12]`}
                disabled={handleButtonDisablity() || isExperimentsLoading}
                onPress={handleSubmit}
                style={{
                  backgroundColor: handleButtonDisablity()
                    ? colors.grays[50]
                    : colors.primary[100],
                }}
                loading={isExperimentsLoading}
                loaderText={i18n.t('verifySecondaryPhNum.verifying')}
              >
                {i18n.t('verifySecondaryPhNum.verify')}
              </Button>
            </Stack>
          </>
        }
      >
        <Stack className="mx-3 mb-3 mt-2">
          <H5 className="text-grays-100 font-[Graphie-Bold]">
            {i18n.t('verifySecondaryPhNum.getPermission')}
          </H5>
          <B1 className="text-grays-70 py-1">
            {i18n.t('verifySecondaryPhNum.otpSent')}
          </B1>
          <XStack>
            <SH1>{selectedCustomer?.secondaryPhoneNumber}</SH1>
            <Pressable onPress={editPhoneNumber}>
              <SH1 className="text-primary-100 pl-2 underline">
                {i18n.t('verifySecondaryPhNum.edit')}
              </SH1>
            </Pressable>
          </XStack>
        </Stack>
        <Stack className="mt-2 items-center justify-center">
          <CodeField
            ref={ref}
            {...props}
            caretHidden={true}
            value={value}
            onChangeText={(i) => {
              setValue(i)
            }}
            cellCount={4}
            rootStyle={styles.codeFieldRoot}
            keyboardType="number-pad"
            textContentType="oneTimeCode"
            renderCell={({ index, symbol, isFocused }) => (
              <Stack
                key={index}
                style={[styles.cell, isFocused && styles.focusCell]}
              >
                <Text key={index} onLayout={getCellOnLayoutHandler(index)}>
                  {symbol || (isFocused ? <Cursor /> : null)}
                </Text>
              </Stack>
            )}
          />
        </Stack>
        <Stack className="mt-4 items-center">
          {timer === 0 || disableResend ? (
            <Pressable
              onPress={onPressResendButton}
              eventName="click_action"
              eventPayload={{
                item_name: 'verify_secondary_phone_otp',
              }}
            >
              <SH3 className="text-primary-100">
                {i18n.t('verifySecondaryPhNum.resendOtp')}
              </SH3>
            </Pressable>
          ) : (
            <B2 className=" text-grays-60 text-center">
              {`Resend OTP in `}
              <SH3 className="text-primary-100">
                00:{timer < 10 ? `0${timer}` : timer}
              </SH3>
            </B2>
          )}
        </Stack>
      </KeyBoardWrapper>
    </LazyHOC>
  )
}
export default VerifySecondaryPhoneOtp

const styles = StyleSheet.create({
  codeFieldRoot: {
    alignItems: 'center',
  },
  cell: {
    width: 48,
    height: 48,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 2,
    marginHorizontal: 10,
    borderColor: colors.grays[40],
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
  },
  focusCell: {
    borderColor: colors.primary[50],
  },
})
